import { BACKEND_ROOT } from '../../config/host-config';
import { BaseAuthService } from './BaseAuthService';
import { HasId } from '../../interfaces';
import { NotificationService } from '../../services';

export class BaseDataService<T extends HasId> extends BaseAuthService {
    protected apiRoot: string;
    protected endpointRoot: string;
    protected entityType: string;
    protected entityTypePlural: string;
    protected notificationService: NotificationService;

    // the endpoint parameter is only used if the entityType display name provided does not match the API controller endpoint
    constructor(entityType: string, entityTypePlural: string, notificationService: NotificationService, endpoint?: string) {
        super();
        this.entityType = entityType;
        this.entityTypePlural = entityTypePlural;

        this.apiRoot = `${BACKEND_ROOT}/api`;

        if (endpoint) {
            this.endpointRoot = `${this.apiRoot}/${endpoint}`;
        } else {
            // remove all whitespace from entity type, otherwise it will mess up the api endpoint
            this.endpointRoot = `${this.apiRoot}/${entityType.replace(/\s/g, '')}`;
        }

        this.notificationService = notificationService;
    }

    public get(id: number): Promise<any> {
        const endpoint = `${this.endpointRoot}/${id}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }

            const message = `Error retrieving ${this.entityType.toLowerCase()}.`;
            this.notificationService.addError(message);
            throw response;
        });
    }

    public getAll(): Promise<any> {
        const endpoint = `${this.endpointRoot}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }

            const message = `Error retrieving ${this.entityTypePlural.toLowerCase()}.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    public getAllPaged(page: number, pageSize: number, filter?: string, sortProperty?: string, sortDirection?: string, licenseesOnly?: boolean, subscriptionsOnly?: boolean) {
        let endpoint = `${this.endpointRoot}/paged?page=${page}&pageSize=${pageSize}`;
        const init = this.buildRequestInit('GET');

        if (filter !== undefined) {
            endpoint = `${endpoint}&filter=${filter}`;
        }

        if (sortProperty !== undefined) {
            endpoint = `${endpoint}&sortProperty=${sortProperty}`;

            if (sortDirection !== undefined) {
                endpoint = `${endpoint}&sortDirection=${sortDirection}`;
            } else {
                endpoint = `${endpoint}&sortDirection=asc`;
            }
        }

        if (licenseesOnly !== undefined) {
            endpoint = `${endpoint}&licenseesOnly=${licenseesOnly}`;
        }

        if (subscriptionsOnly !== undefined) {
            endpoint = `${endpoint}&subscriptionsOnly=${subscriptionsOnly}`;
        }

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }

            const message = `Error retrieving ${this.entityTypePlural.toLowerCase()}.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    public update(entity: T): Promise<any> {
        const endpoint = `${this.endpointRoot}/${entity.id}`;
        const init = this.buildRequestInit('PUT', entity);

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                this.notificationService.addNotification(`${this.entityType} updated successfully.`);
                return response.json();
            }

            const message = `Error updating ${this.entityType.toLowerCase()}.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    public create(entity: T): Promise<any> {
        const endpoint = `${this.endpointRoot}`;
        const init = this.buildRequestInit('POST', entity);

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                this.notificationService.addNotification(`${this.entityType} created successfully.`);
                return response.json();
            }

            const message = `Error creating ${this.entityType.toLowerCase()}.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    public delete(entity: T): Promise<any> {
        const endpoint = `${this.endpointRoot}/${entity.id}`;
        const init = this.buildRequestInit('DELETE');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                this.notificationService.addNotification(`${this.entityType} deleted successfully.`);
                return;
            }

            const message = `Error deleting ${this.entityType.toLowerCase()}.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }
}


// WEBPACK FOOTER //
// ./src/services/api/BaseDataService.tsx